import React from 'react';
import { Link } from 'react-router-dom';
import { FaHome, FaChevronRight, FaHeadset, FaClock, FaUserTie, FaCheckCircle, FaArrowRight, FaPhone, FaLaptop, FaTools, FaShieldAlt } from 'react-icons/fa';
import Navigation from '../components/Navigation';
import TopBar from '../components/TopBar';
import Footer from '../components/Footer';

const ITSupportPage = () => {
    const services = [
        {
            icon: <FaHeadset size={24} />,
            title: 'Help Desk Support',
            description: 'Round-the-clock remote technical support for immediate issue resolution and troubleshooting.',
            gradient: 'from-green-500 to-emerald-500'
        },
        {
            icon: <FaUserTie size={24} />,
            title: 'On-Site Technicians',
            description: 'Expert technicians deployed to your location for hands-on support when needed.',
            gradient: 'from-blue-600 to-cyan-600'
        },
        {
            icon: <FaClock size={24} />,
            title: '24/7 Availability',
            description: 'Always-on support ensuring your systems run smoothly around the clock.',
            gradient: 'from-purple-600 to-indigo-600'
        },
        {
            icon: <FaLaptop size={24} />,
            title: 'Remote Monitoring',
            description: 'Proactive system monitoring to identify and resolve issues before they impact your business.',
            gradient: 'from-orange-500 to-red-500'
        },
        {
            icon: <FaTools size={24} />,
            title: 'Issue Escalation',
            description: 'Structured escalation procedures ensuring complex issues reach the right experts quickly.',
            gradient: 'from-teal-500 to-cyan-500'
        },
        {
            icon: <FaShieldAlt size={24} />,
            title: 'Security Support',
            description: 'Security incident response and ongoing protection for your IT infrastructure.',
            gradient: 'from-rose-500 to-pink-500'
        }
    ];

    const stats = [
        { number: '< 15min', label: 'Average Response Time' },
        { number: '95%', label: 'First Call Resolution' },
        { number: '24/7/365', label: 'Support Availability' },
        { number: '98%', label: 'Customer Satisfaction' }
    ];

    return (
        <div className="font-sans">
            <TopBar />
            <Navigation />

            {/* Hero Section */}
            <section className="relative bg-gradient-to-br from-gray-900 via-green-900 to-emerald-900 py-24 overflow-hidden">
                <div className="absolute inset-0 opacity-10">
                    <div className="absolute inset-0" style={{
                        backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
                    }}></div>
                </div>

                <div className="absolute top-0 right-0 w-96 h-96 bg-green-500 rounded-full filter blur-3xl opacity-20 translate-x-1/2 -translate-y-1/2"></div>
                <div className="absolute bottom-0 left-0 w-96 h-96 bg-emerald-500 rounded-full filter blur-3xl opacity-20 -translate-x-1/2 translate-y-1/2"></div>

                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
                    <nav className="flex items-center gap-2 text-sm text-gray-300 mb-8">
                        <Link to="/" className="flex items-center gap-1 hover:text-white transition-colors">
                            <FaHome size={14} />
                            <span>Home</span>
                        </Link>
                        <FaChevronRight size={10} className="text-gray-500" />
                        <span className="text-green-400">24/7 IT Support</span>
                    </nav>

                    <div className="grid lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6 leading-tight">
                                <span className="text-transparent bg-clip-text bg-gradient-to-r from-green-400 to-emerald-400">24/7</span> IT Support
                            </h1>
                            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
                                Never face IT issues alone. Our expert support team is available around the clock to keep your business running smoothly.
                            </p>
                            <div className="flex flex-wrap gap-4">
                                <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-green-500 to-emerald-500 text-white rounded-xl font-semibold hover:shadow-lg hover:shadow-green-500/30 transition-all duration-300">
                                    Get Support Now
                                    <FaArrowRight size={14} />
                                </Link>
                                <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white/30 text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                                    <FaPhone size={14} />
                                    Call Us Now
                                </a>
                            </div>
                        </div>
                        <div className="relative">
                            <div className="bg-gradient-to-br from-green-600/20 to-emerald-600/20 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
                                <img
                                    src="https://images.unsplash.com/photo-1553877522-43269d4ea984?w=600&h=400&fit=crop"
                                    alt="IT Support"
                                    className="w-full h-80 object-cover rounded-xl shadow-2xl"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Stats Section */}
            <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
                <div className="max-w-7xl mx-auto">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
                        {stats.map((stat, index) => (
                            <div key={index} className="text-center">
                                <div className="text-3xl md:text-4xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-green-500 to-emerald-500 mb-2">
                                    {stat.number}
                                </div>
                                <div className="text-sm md:text-base text-gray-600">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
                <div className="max-w-7xl mx-auto">
                    <div className="text-center mb-16">
                        <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">Our Services</span>
                        <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">Comprehensive IT Support</h2>
                        <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                            From help desk to on-site support, we've got you covered
                        </p>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                        {services.map((service, index) => (
                            <div
                                key={index}
                                className="group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 hover:border-green-200"
                            >
                                <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${service.gradient} flex items-center justify-center text-white mb-6 group-hover:scale-110 transition-transform duration-300`}>
                                    {service.icon}
                                </div>
                                <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                                <p className="text-gray-600 leading-relaxed">{service.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Benefits Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-gray-50 to-green-50">
                <div className="max-w-7xl mx-auto">
                    <div className="grid lg:grid-cols-2 gap-16 items-center">
                        <div>
                            <img
                                src="https://images.unsplash.com/photo-1521791136064-7986c2920216?w=600&h=500&fit=crop"
                                alt="Support Team"
                                className="w-full h-auto rounded-2xl shadow-2xl"
                            />
                        </div>
                        <div>
                            <span className="inline-block px-4 py-2 bg-green-100 text-green-700 rounded-full text-sm font-semibold mb-4">Why Choose Us</span>
                            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">Your Trusted IT Partner</h2>
                            <p className="text-lg text-gray-600 mb-8">
                                We combine technical expertise with genuine care for your business success. Our support team becomes an extension of your organization.
                            </p>
                            <div className="space-y-4">
                                {['Rapid response times', 'Certified IT professionals', 'Proactive issue prevention', 'Detailed reporting', 'Flexible support plans', 'Industry expertise'].map((item, index) => (
                                    <div key={index} className="flex items-center gap-3">
                                        <FaCheckCircle className="text-green-500 flex-shrink-0" size={20} />
                                        <span className="text-gray-700 font-medium">{item}</span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* CTA Section */}
            <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-green-500 to-emerald-500">
                <div className="max-w-4xl mx-auto text-center">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">Need IT Support Right Now?</h2>
                    <p className="text-xl text-green-100 mb-8">
                        Our team is standing by to help you resolve any IT issue, anytime.
                    </p>
                    <div className="flex flex-wrap justify-center gap-4">
                        <Link to="/contact" className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 rounded-xl font-semibold hover:shadow-lg transition-all duration-300">
                            Contact Support
                            <FaArrowRight size={14} />
                        </Link>
                        <a href="tel:+12014099845" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-white text-white rounded-xl font-semibold hover:bg-white/10 transition-all duration-300">
                            <FaPhone size={14} />
                            Call 24/7 Hotline
                        </a>
                    </div>
                </div>
            </section>

            <Footer />
        </div>
    );
};

export default ITSupportPage;
